//srs/services/alarmHelper.js

/**
 * Alarm Helper for MeterSuite
 * Driver for alarm detection across protocols:
 * - LoRa WBus (Frame 0x80 and 0x81)
 * - DLMS
 * - WMBus/OMS
 */

const OK_DIAGNOSTICS = ['OK', 'NO_ERROR', 'NO_ALARM', 'NORMAL', ''];

function formatAlarmLabel(alarm) {
    if (alarm == null) return '';

    if (typeof alarm === 'string' || typeof alarm === 'number' || typeof alarm === 'boolean') {
        return String(alarm).trim();
    }

    if (typeof alarm === 'object') {
        const name = String(alarm.name || alarm.Name || alarm.code || alarm.Code || '').trim();
        const description = String(
            alarm.description ||
            alarm.Description ||
            alarm.label ||
            alarm.Label ||
            ''
        ).trim();

        if (description && description !== name) return description;
        if (name) return name;
    }

    return '';
}

/**
 * Detects the protocol type from a reading object
 */
export function detectProtocol(reading) {
    if (!reading) return 'unknown';

    const additionalData = reading.additional_data;

    // LoRa frames (0x80 or 0x81)
    if (additionalData?.MessageType === 128 || additionalData?.MessageType === 129 ||
        additionalData?.MessageType === 0x80 || additionalData?.MessageType === 0x81) {
        return 'lora';
    }

    // WMBus (OMS)
    if (additionalData?.name === 'OMS' || additionalData?.media === 'water') {
        return 'wmbus';
    }

    // DLMS
    if (reading.readed_by === 'dlms_server') {
        return 'dlms';
    }

    // Fallback checks
    if (additionalData?.Alarms !== undefined) {
        return 'lora';
    }

    if (reading.current_diagnostic) {
        return 'dlms';
    }

    return 'unknown';
}

/**
 * Get alarm status from any reading
 * @param {Object} reading - The reading object from any protocol
 * @returns {Object} - { hasAlarms, alarms, protocol }
 */
export function getAlarmStatus(reading) {
    if (!reading) {
        return { hasAlarms: false, alarms: [], protocol: 'unknown' };
    }

    const protocol = detectProtocol(reading);

    switch (protocol) {
        case 'lora':
            return getLoraAlarms(reading, protocol);
        case 'dlms':
            return getDlmsAlarms(reading, protocol);
        case 'wmbus':
            return getWmbusAlarms(reading, protocol);
        default:
            return { hasAlarms: false, alarms: [], protocol };
    }
}

/**
 * LoRa: Alarms in additional_data.Alarms.Active[]
 */
function getLoraAlarms(reading, protocol) {
    const alarms = reading.additional_data?.Alarms;

    if (alarms?.Active?.length > 0) {
        return {
            hasAlarms: true,
            alarms: alarms.Active.map(formatAlarmLabel).filter(Boolean),
            protocol
        };
    }

    if (alarms?.Bitmap > 0) {
        return { hasAlarms: true, alarms: [], protocol };
    }

    return { hasAlarms: false, alarms: [], protocol };
}

/**
 * DLMS: Alarms in current_diagnostic
 */
function getDlmsAlarms(reading, protocol) {
    const diagnostic = reading.current_diagnostic;

    if (diagnostic && !OK_DIAGNOSTICS.includes(diagnostic.toUpperCase())) {
        return { hasAlarms: true, alarms: [diagnostic], protocol };
    }

    return { hasAlarms: false, alarms: [], protocol };
}

/**
 * WMBus: Alarms in current_diagnostic + additional_data.status
 */
function getWmbusAlarms(reading, protocol) {
    const alarms = [];

    const diagnostic = reading.current_diagnostic;
    if (diagnostic && !OK_DIAGNOSTICS.includes(diagnostic.toUpperCase())) {
        alarms.push(diagnostic);
    }

    const status = reading.additional_data?.status;
    if (status && !OK_DIAGNOSTICS.includes(status.toUpperCase()) && status !== diagnostic) {
        alarms.push(status);
    }

    return { hasAlarms: alarms.length > 0, alarms, protocol };
}

export default {
    getAlarmStatus,
    detectProtocol
};
